import { useMemo } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import AllTrucksItem from './AllTrucksItem';
import { useGetAllTrucks } from 'domains/admin/hooks/useGetAllTrucks';
import { useTranslation } from 'i18n/TranslationProvider';
import { useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';
import {
  UnifiedEmptyState,
  UnifiedFilterBar,
  UnifiedLoadingState,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';

const getExpiryWindow = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const inTwoWeeks = new Date(today);
  inTwoWeeks.setDate(inTwoWeeks.getDate() + 14);
  return { today, inTwoWeeks };
};

const isExpiringSoon = (truck) => {
  if (!truck?.cmrInsuranceExpiryDate) return false;
  const expiry = new Date(truck.cmrInsuranceExpiryDate);
  if (Number.isNaN(expiry.getTime())) return false;
  expiry.setHours(0, 0, 0, 0);
  const { today, inTwoWeeks } = getExpiryWindow();
  return expiry >= today && expiry <= inTwoWeeks;
};

const normalize = (value) => String(value || '').toLowerCase().trim();

const AllTrucksList = () => {
  const { t } = useTranslation();
  const { trucks, loading, error, refetch } = useGetAllTrucks();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tableSx } = useUnifiedTableStyles();

  const panelBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(13,23,53,0.72)');
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  const filter = normalize(searchParams.get('status') || 'all');
  const query = searchParams.get('q') || '';
  const page = Math.max(Number.parseInt(searchParams.get('page') || '1', 10), 1);
  const limit = Math.max(Number.parseInt(searchParams.get('limit') || '25', 10), 10);

  const setParam = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  };

  const counts = useMemo(() => {
    const list = Array.isArray(trucks) ? trucks : [];
    return {
      all: list.length,
      approved: list.filter((truck) => truck.validationStatus === 'approved').length,
      pending: list.filter((truck) => truck.validationStatus === 'pending').length,
      disabled: list.filter((truck) => truck.validationStatus === 'disabled').length,
      rejected: list.filter((truck) => truck.validationStatus === 'rejected').length,
      expiringSoon: list.filter((truck) => isExpiringSoon(truck)).length,
    };
  }, [trucks]);

  const filteredTrucks = useMemo(() => {
    const list = Array.isArray(trucks) ? trucks : [];
    return list.filter((truck) => {
      if (filter !== 'all') {
        if (filter === 'expiringsoon') {
          if (!isExpiringSoon(truck)) return false;
        } else if (normalize(truck.validationStatus) !== filter) {
          return false;
        }
      }

      if (!query.trim()) return true;
      const haystack = [
        truck.licensePlate,
        truck.ownerId?.displayName,
        truck.ownerId?.email,
        truck.ownerId?.companyName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query.trim().toLowerCase());
    });
  }, [trucks, filter, query]);

  const total = filteredTrucks.length;
  const totalPages = Math.ceil(total / limit) || 0;
  const currentPage = Math.min(page, totalPages || 1);
  const startIndex = (currentPage - 1) * limit;
  const pagedTrucks = filteredTrucks.slice(startIndex, startIndex + limit);
  const visibleFrom = total === 0 ? 0 : startIndex + 1;
  const visibleTo = total === 0 ? 0 : Math.min(startIndex + limit, total);

  if (loading) {
    return (
      <UnifiedSurfaceCard p={8}>
        <UnifiedLoadingState label={t('Loading trucks...')} minH="120px" />
      </UnifiedSurfaceCard>
    );
  }

  if (error) {
    return (
      <UnifiedSurfaceCard p={5}>
        <Alert status="error" borderRadius="12px">
          <AlertIcon />
          {t('Error fetching trucks')}: {error}
        </Alert>
      </UnifiedSurfaceCard>
    );
  }

  return (
    <Stack spacing={4}>
      <UnifiedSurfaceCard
        p={5}
        bg={panelBg}
      >
        <Stack spacing={4}>
          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap">
            <Text fontWeight="700">{t('Manage All Trucks')}</Text>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              {t('Refresh')}
            </Button>
          </Flex>

          <UnifiedFilterBar>
            <Box minW={{ base: '100%', md: '260px' }}>
              <Input
                value={query}
                placeholder={t('Search license plate or owner')}
                onChange={(event) => setParam({ q: event.target.value, page: 1 })}
              />
            </Box>
            <Box minW={{ base: '100%', md: '210px' }}>
              <Select
                value={filter}
                onChange={(event) => setParam({ status: event.target.value, page: 1 })}
              >
                <option value="all">{t('All')}</option>
                <option value="approved">{t('Approved')}</option>
                <option value="pending">{t('Pending')}</option>
                <option value="disabled">{t('Disabled')}</option>
                <option value="rejected">{t('Rejected')}</option>
                <option value="expiringsoon">{t('Expire Soon (14 days)')}</option>
              </Select>
            </Box>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setParam({ q: '', status: 'all', page: 1 })}
            >
              {t('Reset')}
            </Button>
          </UnifiedFilterBar>

          <HStack spacing={2} flexWrap="wrap">
            <BadgeStat label={t('All')} value={counts.all} color="blue" />
            <BadgeStat label={t('Approved')} value={counts.approved} color="green" />
            <BadgeStat label={t('Pending')} value={counts.pending} color="orange" />
            <BadgeStat label={t('Disabled')} value={counts.disabled} color="gray" />
            <BadgeStat label={t('Rejected')} value={counts.rejected} color="red" />
            <BadgeStat label={t('Expire Soon (14 days)')} value={counts.expiringSoon} color="yellow" />
          </HStack>
        </Stack>
      </UnifiedSurfaceCard>

      {total === 0 ? (
        <UnifiedSurfaceCard p={4}>
          <UnifiedEmptyState
            title={t('No trucks found for this filter.')}
            description={t('Try broadening the current search or status filter.')}
            minH="140px"
          />
        </UnifiedSurfaceCard>
      ) : (
        <UnifiedSurfaceCard p={4} overflow="hidden">
          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={3} gap={2} flexWrap="wrap">
            <Text fontSize="sm" color={mutedText}>
              {t('Showing')} {visibleFrom}-{visibleTo} {t('of')} {total}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color={mutedText}>{t('Rows')}</Text>
              <Select
                size="sm"
                value={String(limit)}
                onChange={(event) => setParam({ limit: event.target.value, page: 1 })}
                w="92px"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Select>
            </HStack>
          </Flex>

          <Box overflowX="auto">
            <Table size="sm" sx={{ minWidth: '1280px', ...tableSx }}>
              <Thead>
                <Tr>
                  <Th>{t('License Plate')}</Th>
                  <Th>{t('Owner')}</Th>
                  <Th>{t('Status')}</Th>
                  <Th>{t('CMR Expiry')}</Th>
                  <Th>{t('CMR Document')}</Th>
                  <Th>{t('Last Rejection')}</Th>
                  <Th>{t('Actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pagedTrucks.map((truck) => (
                  <AllTrucksItem
                    key={truck.id || truck._id}
                    truck={truck}
                    refetch={refetch}
                    tableMode
                  />
                ))}
              </Tbody>
            </Table>
          </Box>

          <Flex mt={4} justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap">
            <Text fontSize="sm" color={mutedText}>
              {t('Total')}: {total}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParam({ page: currentPage - 1 })}
                isDisabled={currentPage <= 1}
              >
                {t('Previous')}
              </Button>
              <Text fontSize="sm" minW="100px" textAlign="center">
                {t('Page')} {currentPage}
                {totalPages ? ` / ${totalPages}` : ''}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParam({ page: currentPage + 1 })}
                isDisabled={!totalPages || currentPage >= totalPages}
              >
                {t('Next')}
              </Button>
            </HStack>
          </Flex>
        </UnifiedSurfaceCard>
      )}
    </Stack>
  );
};

const BadgeStat = ({ label, value, color }) => (
  <Box
    as="span"
    px={3}
    py={1}
    borderRadius="full"
    fontSize="sm"
    fontWeight="600"
    bg={`${color}.100`}
    color={`${color}.700`}
  >
    {label}: {value}
  </Box>
);

export default AllTrucksList;
